import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Holds the unread anomaly count shown on the rail's Notification badge, and the intent to open
 * the Alerts tray.
 *
 * This is deliberately a seam, not a fetch: the rail needs a count and a click target, while the
 * Alerts tray and its endpoint are owned by the tray ticket. That ticket subscribes
 * `openTrayRequested$` and calls `setUnreadCount` / `markAllReviewed` as alerts are reviewed —
 * the rail never changes again.
 *
 * The count starts at 0, so the badge stays hidden until something sets it.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private readonly _unreadCount = new BehaviorSubject<number>(0);

  /** Unread anomaly count. Emits on every change so the badge stays live. */
  public readonly unreadCount$: Observable<number> = this._unreadCount.asObservable();

  private readonly _openTrayRequested = new Subject<void>();

  /** Emits when the user activates the Notification rail item. */
  public readonly openTrayRequested$: Observable<void> = this._openTrayRequested.asObservable();

  /** Sets the unread count. Negatives are clamped to 0 so the badge can never show a negative. */
  setUnreadCount(count: number): void {
    this._unreadCount.next(this.clampToZero(count));
  }

  /** Clears the unread count once every waiting alert has been reviewed. */
  markAllReviewed(): void {
    this._unreadCount.next(0);
  }

  /** Current unread count without subscribing. */
  getUnreadCount(): number {
    return this._unreadCount.value;
  }

  /** Asks the host to open the Alerts tray. */
  requestOpenTray(): void {
    this._openTrayRequested.next();
  }

  private clampToZero(count: number): number {
    return count > 0 ? count : 0;
  }
}
